import { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  ControlledInput,
  ControlledTextArea,
  DraggableItem,
  FlyupEditingModal,
  SimpleSelectableCard,
  Sortable,
} from "shared/components";
import { Divider, Flex, Grid } from "styled-system/jsx";
import { QuestionFormValues } from "./useCreateEditQuestionnaireModalForm";
import {
  Button,
  LinkButton,
  Select,
  SimpleDialog,
  Switch,
  Text,
  useToastContext,
} from "@ttc3k/trekker";
import { useCreateEditQuestionForm } from "./useCreateEditQuestionForm";
import { FormProvider, useFieldArray, useWatch } from "react-hook-form";
import {
  BookerByIdDocument,
  EnumSurveyQuestionType,
  SurveyManyDocument,
  useSurveyQuestionUpdateMutation,
} from "gql/generated";
import {
  AlignLeft,
  Calendar,
  CheckCircle,
  Map as MapIcon,
  NavArrowDown,
  PageEdit,
  Search,
  SwitchOn,
  Text as TextIcon,
  TextSize,
  TextSquare,
  Xmark,
} from "iconoir-react";
import { isSurveyDesignFieldType } from "shared/components/organisms/SurveyQuestionInput/surveyQuestionDesign";
import { verticalListSortingStrategy } from "@dnd-kit/sortable";
import { usePostHog } from "shared/hooks";
import { useFeatureFlag } from "shared/contextProviders/featureFlags/useFeatureFlag";
import { FeatureFlags } from "shared/featureFlags";

const ALWAYS_SHOW_VALUE = "__always__";

type CreateEditQuestionFlyupProps = {
  isOpen: boolean;
  onClose: () => void;
  question: QuestionFormValues | null;
  onEditSuccess: (quesiton: QuestionFormValues) => void;
  onCreateSuccess: (question: QuestionFormValues) => void;
  onDeleteQuestion: (question: QuestionFormValues) => void;
  variant?: "questionnaire" | "form";
  hiddenQuestionTypes?: EnumSurveyQuestionType[];
  /** Toggle a new question starts out as a follow-up of. */
  defaultParentQuestion?: string;
  /** Toggles in this questionnaire a question can be a follow-up of. */
  toggleOptions?: { id: string; name: string }[];
  /** Toggles can be asked once per business (broadcast forms). */
  allowPerBusinessToggles?: boolean;
};

const inputFieldTypeOptions = [
  {
    labelKey: "LABELS.SHORT_TEXT",
    icon: TextSquare,
    type: EnumSurveyQuestionType.ShortText,
  },
  {
    labelKey: "LABELS.LONG_TEXT",
    icon: AlignLeft,
    type: EnumSurveyQuestionType.LongText,
  },
  {
    labelKey: "LABELS.RICH_TEXT",
    icon: PageEdit,
    type: EnumSurveyQuestionType.RichText,
  },
  {
    labelKey: "LABELS.SELECT",
    icon: NavArrowDown,
    type: EnumSurveyQuestionType.Select,
  },
  {
    labelKey: "LABELS.BOOLEAN",
    icon: CheckCircle,
    type: EnumSurveyQuestionType.Boolean,
  },
  {
    labelKey: "LABELS.WHEREABOUTS_SEARCH",
    icon: Search,
    type: EnumSurveyQuestionType.WhereaboutsSearch,
  },
  {
    labelKey: "LABELS.ADDRESS_SEARCH",
    icon: MapIcon,
    type: EnumSurveyQuestionType.AddressSearch,
  },
  {
    labelKey: "LABELS.DATE",
    icon: Calendar,
    type: EnumSurveyQuestionType.Date,
  },
  {
    labelKey: "LABELS.TOGGLE",
    icon: SwitchOn,
    type: EnumSurveyQuestionType.Toggle,
  },
];

const designFieldTypeOptions = [
  {
    labelKey: "LABELS.SECTION_HEADING",
    icon: TextSize,
    type: EnumSurveyQuestionType.SectionHeading,
  },
  {
    labelKey: "LABELS.CUSTOM_TEXT",
    icon: TextIcon,
    type: EnumSurveyQuestionType.CustomText,
  },
];

/** Temporarily hidden for new questions; still shown when editing an existing question of that type. */
const STOREFRONT_TEMPORARILY_HIDDEN_QUESTION_TYPES: EnumSurveyQuestionType[] = [
  EnumSurveyQuestionType.RichText,
  EnumSurveyQuestionType.WhereaboutsSearch,
];

export const CreateEditQuestionFlyup = ({
  isOpen,
  onClose,
  question,
  onEditSuccess,
  onCreateSuccess,
  onDeleteQuestion,
  variant = "questionnaire",
  hiddenQuestionTypes = [],
  defaultParentQuestion,
  toggleOptions = [],
  allowPerBusinessToggles = false,
}: CreateEditQuestionFlyupProps) => {
  const conditionalQuestionsEnabled = useFeatureFlag(
    FeatureFlags.ConditionalQuestions,
  );
  const { t: tCore } = useTranslation("core");
  const { t } = useTranslation("storefront", {
    keyPrefix:
      "BOOKING_WIDGET_SETUP.QUESTIONNAIRE.CREATE_EDIT_MODAL.CREATE_EDIT_QUESTION",
  });
  const { toastFactory } = useToastContext();
  const { trackPostHogException } = usePostHog();

  const isEditingQuestion = question && question._id.length === 24;

  const { form, onSubmit } = useCreateEditQuestionForm({
    question,
    defaultParentQuestion,
    onSuccess: data => {
      handleClose();
      if (isEditingQuestion) {
        onEditSuccess(data);
      } else {
        onCreateSuccess(data);
      }
    },
    onError: () => {
      trackPostHogException(new Error("Error submitting question form"), {
        fileName: "CreateEditQuestionFlyup.tsx",
        method: "onError",
        message: "Error submitting question form",
      });
      toastFactory.create({
        title: tCore("ERROR.TITLE"),
        description: t("ERRORS.FAILED_TO_SAVE"),
      });
    },
  });
  const [updateQuestion, { loading: loadingDeletion }] =
    useSurveyQuestionUpdateMutation({
      refetchQueries: [BookerByIdDocument, SurveyManyDocument],
      awaitRefetchQueries: true,
    });

  const [isDeleteConfirmationModalOpen, setIsDeleteConfirmationModalOpen] =
    useState(false);

  const handleClose = () => {
    onClose();
    form.reset();
  };

  const handleDeleteConfirmationModalActionClick = async (action: boolean) => {
    //  If the user confirms the deletion, set the question as inactive
    if (action) {
      form.setValue("isActive", false);
      try {
        setIsDeleteConfirmationModalOpen(false);
        // If the question is being deleted & already exists in the DB,
        // do not submit the form, but instead update the question in the DB
        // directly
        if (isEditingQuestion) {
          await updateQuestion({
            variables: {
              id: question._id,
              record: {
                isActive: false,
              },
            },
          });
        }

        // If the question is being deleted & does not exist in the DB,
        // reset the form & close the modal
        handleClose();
        onDeleteQuestion(form.getValues());
      } catch {
        trackPostHogException(new Error("Error submitting question form"), {
          fileName: "CreateEditQuestionFlyup.tsx",
          method: "onError",
          message: "Error submitting question form",
        });
        toastFactory.create({
          title: tCore("ERROR.TITLE"),
          description: t("ERRORS.FAILED_TO_SAVE"),
        });
        setIsDeleteConfirmationModalOpen(false);
      }
    } else {
      // If the user cancels the deletion, close the modal & do nothing else
      setIsDeleteConfirmationModalOpen(false);
    }
  };

  const watchedIsRequired = useWatch({
    control: form.control,
    name: "required",
  });
  const watchedQuestionType = useWatch({
    control: form.control,
    name: "type",
  });
  const watchedOptions = useWatch({ control: form.control, name: "options" });
  const activeOptions = watchedOptions.filter(o => o.isActive);
  // Using useFieldArray (rather than plain form.setValue calls) for structural
  // changes to "options" (add/remove/reorder) ensures each option's typed label
  // stays attached to the correct row when the options are dragged into a new
  // order, instead of getting cleared out.
  const {
    append: appendOption,
    remove: removeOption,
    update: updateOption,
    move: moveOption,
    replace: replaceOptions,
  } = useFieldArray({ control: form.control, name: "options" });
  const questionTypeSupportsOptions =
    watchedQuestionType === EnumSurveyQuestionType.Select ||
    watchedQuestionType === EnumSurveyQuestionType.Boolean;

  const filterTypeOptions = useCallback(
    (options: typeof inputFieldTypeOptions) => {
      const hidden = new Set<EnumSurveyQuestionType>([
        ...(variant === "questionnaire"
          ? STOREFRONT_TEMPORARILY_HIDDEN_QUESTION_TYPES
          : []),
        ...hiddenQuestionTypes,
        ...(conditionalQuestionsEnabled ? [] : [EnumSurveyQuestionType.Toggle]),
      ]);
      return options.filter(
        opt => !hidden.has(opt.type) || opt.type === watchedQuestionType,
      );
    },
    [
      conditionalQuestionsEnabled,
      hiddenQuestionTypes,
      watchedQuestionType,
      variant,
    ],
  );

  const inputFieldPickerOptions = useMemo(
    () => filterTypeOptions(inputFieldTypeOptions),
    [filterTypeOptions],
  );
  const designFieldPickerOptions = useMemo(
    () => filterTypeOptions(designFieldTypeOptions),
    [filterTypeOptions],
  );

  const isDesignField = isSurveyDesignFieldType(watchedQuestionType);
  const isToggle = watchedQuestionType === EnumSurveyQuestionType.Toggle;
  const watchedBusinessScope = useWatch({
    control: form.control,
    name: "businessScope",
  });
  const showBusinessScopeSwitch = isToggle && allowPerBusinessToggles;
  const watchedParentQuestion = useWatch({
    control: form.control,
    name: "parentQuestion",
  });
  const parentSelectCollection = useMemo(
    () =>
      Select.createCollection({
        items: [
          { label: t("LABELS.ALWAYS_SHOW"), value: ALWAYS_SHOW_VALUE },
          ...toggleOptions
            .filter(option => option.id !== question?._id)
            .map(option => ({ label: option.name, value: option.id })),
        ],
      }),
    [question?._id, t, toggleOptions],
  );
  const showParentSelect =
    conditionalQuestionsEnabled &&
    !isDesignField &&
    !isToggle &&
    parentSelectCollection.items.length > 1;
  const contentLabelKey =
    watchedQuestionType === EnumSurveyQuestionType.SectionHeading
      ? "LABELS.SECTION_HEADING_TEXT"
      : watchedQuestionType === EnumSurveyQuestionType.CustomText
        ? "LABELS.CUSTOM_TEXT_CONTENT"
        : "LABELS.QUESTION";

  const formId = "question-form";

  const title = question ? t("TITLE_EDIT") : t("TITLE_CREATE");
  const description = question
    ? t("DESCRIPTION_EDIT")
    : t("DESCRIPTION_CREATE");

  return (
    <>
      <FlyupEditingModal
        isOpen={isOpen}
        onClose={handleClose}
        title={title}
        footerOverride={
          <Flex
            flexDir="row"
            justifyContent="space-between"
            alignItems="center"
            gap="200"
            px="500"
            py="300"
            borderTop="1px solid"
            borderColor="border.light"
            boxShadow="0px 0px 8px 0px rgba(0, 0, 0, 0.10)"
          >
            <Flex gap={"200"} alignItems={"center"}>
              {!isDesignField && !isToggle && (
                <>
                  <Flex gap={"150"} alignItems={"center"}>
                    <Switch
                      label={t("CTA_BUTTONS.REQUIRED")}
                      name="required"
                      checked={watchedIsRequired}
                      onChange={() =>
                        form.setValue("required", !watchedIsRequired, {
                          shouldDirty: true,
                        })
                      }
                    />
                  </Flex>
                  <Divider
                    orientation={"vertical"}
                    height={"20px"}
                    borderColor={"border.light"}
                  />
                </>
              )}
              <Button
                visual="unstyled"
                color={"text.error.mid"}
                _hover={{ color: "text.error.dark" }}
                fontSize={"md"}
                lineHeight={"md"}
                fontWeight={"medium"}
                type={"button"}
                isLoading={loadingDeletion}
                onClick={() => setIsDeleteConfirmationModalOpen(true)}
              >
                {t("CTA_BUTTONS.DELETE")}
              </Button>
            </Flex>
            <Button
              visual="accent.blue"
              type="button"
              disabled={loadingDeletion || !form.formState.isDirty}
              isLoading={form.formState.isSubmitting}
              onClick={() => onSubmit()}
            >
              {question ? t("CTA_BUTTONS.SAVE") : t("CTA_BUTTONS.ADD")}
            </Button>
          </Flex>
        }
      >
        <FormProvider {...form}>
          <form onSubmit={onSubmit} id={formId}>
            <Flex flexDir={"column"} gap={"300"}>
              <Text>{description}</Text>

              <ControlledTextArea
                name={"name"}
                label={t(contentLabelKey)}
                height={isDesignField ? "138px" : "72px"}
              />

              {!isDesignField && (
                <ControlledTextArea
                  name={"description"}
                  label={t(
                    isToggle
                      ? "LABELS.TOGGLE_DESCRIPTION"
                      : "LABELS.DESCRIPTION",
                  )}
                  hint={t(
                    isToggle
                      ? "LABELS.TOGGLE_DESCRIPTION_HINT"
                      : "LABELS.DESCRIPTION_HINT",
                  )}
                  height={"72px"}
                />
              )}

              <Flex flexDir={"column"} gap={"100"}>
                <Text color={"text.mid"} visual={"smallMedium"}>
                  {t("LABELS.ANSWER_TYPE")}
                </Text>
                <Grid gridTemplateColumns={"1fr 1fr"}>
                  {inputFieldPickerOptions.map(option => {
                    const handleSelectQuestionType = () => {
                      if (
                        option.type === EnumSurveyQuestionType.Select &&
                        watchedOptions.length === 0
                      ) {
                        appendOption({
                          label: "",
                          isActive: true,
                          _id: Date.now().toString(36),
                        });
                      }
                      if (
                        option.type === EnumSurveyQuestionType.WhereaboutsSearch
                      ) {
                        form.setValue(
                          "whereaboutsSearchEntityType",
                          "OPERATOR",
                        );
                      }
                      if (option.type === EnumSurveyQuestionType.Toggle) {
                        form.setValue("required", false, { shouldDirty: true });
                        form.setValue("parentQuestion", undefined, {
                          shouldDirty: true,
                        });
                        replaceOptions([]);
                      }
                      form.setValue("type", option.type, {
                        shouldDirty: true,
                      });
                    };
                    return (
                      <SimpleSelectableCard
                        key={option.labelKey}
                        label={t(option.labelKey)}
                        IconElement={option.icon}
                        selected={watchedQuestionType === option.type}
                        onClick={handleSelectQuestionType}
                      />
                    );
                  })}
                </Grid>
              </Flex>

              {designFieldPickerOptions.length > 0 && (
                <Flex flexDir={"column"} gap={"100"}>
                  <Text color={"text.mid"} visual={"smallMedium"}>
                    {t("LABELS.DESIGN_TYPE")}
                  </Text>
                  <Grid gridTemplateColumns={"1fr 1fr"}>
                    {designFieldPickerOptions.map(option => {
                      const handleSelectDesignType = () => {
                        form.setValue("type", option.type, {
                          shouldDirty: true,
                        });
                        form.setValue("required", false, {
                          shouldDirty: true,
                        });
                        form.setValue("parentQuestion", undefined, {
                          shouldDirty: true,
                        });
                        form.setValue("description", undefined, {
                          shouldDirty: true,
                        });
                        replaceOptions([]);
                      };
                      return (
                        <SimpleSelectableCard
                          key={option.labelKey}
                          label={t(option.labelKey)}
                          IconElement={option.icon}
                          selected={watchedQuestionType === option.type}
                          onClick={handleSelectDesignType}
                        />
                      );
                    })}
                  </Grid>
                </Flex>
              )}

              {showBusinessScopeSwitch && (
                <Flex flexDir={"column"} gap={"50"}>
                  <Switch
                    label={t("LABELS.PER_BUSINESS")}
                    checked={watchedBusinessScope === "PER_BUSINESS"}
                    onCheckedChange={e =>
                      form.setValue(
                        "businessScope",
                        e.checked ? "PER_BUSINESS" : "SHARED",
                        { shouldDirty: true },
                      )
                    }
                  />
                  <Text color={"text.mid"} visual={"smallRegular"}>
                    {t("LABELS.PER_BUSINESS_HINT")}
                  </Text>
                </Flex>
              )}

              {showParentSelect && (
                <Select.Root
                  collection={parentSelectCollection}
                  value={[watchedParentQuestion || ALWAYS_SHOW_VALUE]}
                  onValueChange={({ value }: { value: string[] }) =>
                    form.setValue(
                      "parentQuestion",
                      value[0] && value[0] !== ALWAYS_SHOW_VALUE
                        ? value[0]
                        : undefined,
                      { shouldDirty: true },
                    )
                  }
                  label={t("LABELS.SHOW_ONLY_WHEN")}
                  hint={t("LABELS.SHOW_ONLY_WHEN_HINT")}
                >
                  {parentSelectCollection.items.map(item => (
                    <Select.Item key={item.value} item={item} />
                  ))}
                </Select.Root>
              )}

              {questionTypeSupportsOptions && (
                <Flex flexDir={"column"} gap={"200"}>
                  <Text color={"text.mid"} visual={"smallMedium"}>
                    {t("LABELS.CHOICES")}
                  </Text>

                  <Sortable
                    disabled={false}
                    value={watchedOptions}
                    selectorID="_id"
                    sortingStrategy={verticalListSortingStrategy}
                    sortableComponent={(option, i) => {
                      if (!option.isActive) return null;
                      return (
                        <DraggableItem>
                          <Flex
                            gap={"150"}
                            alignItems={"center"}
                            // Prevent the items in this container from starting the drag/sort event
                            onPointerDown={e => e.stopPropagation()}
                            onMouseDown={e => e.stopPropagation()}
                            onTouchStart={e => e.stopPropagation()}
                          >
                            <ControlledInput
                              name={`options.${i}.label`}
                              value={option.label}
                            />
                            {/* Remove option button: */}
                            <Button
                              Icon={Xmark}
                              mode={"light"}
                              disabled={
                                watchedQuestionType ===
                                  EnumSurveyQuestionType.Select &&
                                activeOptions.length <= 1 &&
                                i === 0
                              }
                              type={"button"}
                              onClick={() => {
                                const currentOption = watchedOptions[i];
                                if (currentOption?._id.length === 24) {
                                  // If the option already exists in the DB,
                                  // set it as inactive instead of removing it
                                  updateOption(i, {
                                    ...currentOption,
                                    isActive: false,
                                  });
                                } else {
                                  // Otherwise, remove the option entirely
                                  removeOption(i);
                                }
                              }}
                            />
                          </Flex>
                        </DraggableItem>
                      );
                    }}
                    onChange={async (_newOptions, meta) => {
                      moveOption(meta.oldIndex, meta.newIndex);
                    }}
                    size={"compact"}
                  />
                  {/* Add new option button: */}
                  <LinkButton
                    alignSelf={"flex-start"}
                    onClick={() => {
                      appendOption({
                        label: "",
                        isActive: true,
                        _id: Date.now().toString(36),
                      });
                    }}
                    type={"button"}
                  >
                    + {t("LABELS.ADD_OPTION")}
                  </LinkButton>
                </Flex>
              )}
            </Flex>
          </form>
        </FormProvider>
      </FlyupEditingModal>
      <SimpleDialog
        open={isDeleteConfirmationModalOpen}
        title={t("DELETE.TITLE")}
        message={t("DELETE.MESSAGE")}
        cancelButtonCopy={t("DELETE.CANCEL")}
        onCancelClick={() => handleDeleteConfirmationModalActionClick(false)}
        confirmButtonCopy={t("DELETE.CONFIRM")}
        confirmButtonVisual="destructive"
        onConfirmClick={() => handleDeleteConfirmationModalActionClick(true)}
        size="md"
      />
    </>
  );
};
